import { Discovery } from '@or-sdk/discovery';
import { ref, watch } from 'vue-demi';
import { createSharedComposable, useMounted } from '@vueuse/core';
import { Settings } from '@or-sdk/settings';
import { IdwApi } from '@or-sdk/idw';
import { AccountSettings } from '@or-sdk/account-settings';
import { SERVICE_NAVIGATION_ACCOUNT_SETTING_KEY } from './constants/index.js';

function useServiceDiscoveryUiServices(token, config, feature) {
  let instance;
  const uiServices = ref([]);
  const isMounted = useMounted();
  // API
  const getUiServices = async (invalidate = false) => {
    const res = await instance.listServices({
      type: 'ui',
      feature,
      invalidate
    });
    return res.items;
  };
  const hideService = async serviceKey => {
    const originalServices = [...uiServices.value];
    uiServices.value = originalServices.map(service => service.serviceKey === serviceKey ? {
      ...service,
      envHidden: true
    } : service);
    await instance.hideService(serviceKey);
    uiServices.value = await getUiServices(true);
  };
  const revealService = async serviceKey => {
    const originalServices = [...uiServices.value];
    uiServices.value = originalServices.map(service => service.serviceKey === serviceKey ? {
      ...service,
      envHidden: false
    } : service);
    await instance.revealService(serviceKey);
    uiServices.value = await getUiServices(true);
  };
  const deleteService = async serviceKey => {
    const originalServices = [...uiServices.value];
    uiServices.value = originalServices.filter(service => service.serviceKey !== serviceKey);
    await instance.deleteService(serviceKey);
    uiServices.value = await getUiServices(true);
  };
  // Initialize
  watch([token, isMounted], async ([t, m]) => {
    if (!(t === null || t === void 0 ? void 0 : t.length) || !m) return;
    instance = new Discovery({
      token: t,
      ...config
    });
    uiServices.value = await getUiServices();
  }, {
    immediate: true
  });
  return {
    uiServices,
    deleteService,
    hideService,
    revealService
  };
}
var useServiceDiscoveryUiServices$1 = createSharedComposable(useServiceDiscoveryUiServices);

const USER_SERVICE_NAVIGATION_SETTINGS_URL = 'service-navigation';
const FAVORITES = 'favorites';

function useUserServiceNavigationSettings(token, config, key) {
  const getUserServiceNavigationSettings = ref(() => Promise.resolve(null));
  const setUserServiceNavigationSettings = ref(data => Promise.resolve());
  watch(token, val => {
    if (val === null || val === void 0 ? void 0 : val.length) {
      const instance = new Settings({
        token: val,
        ...config
      });
      getUserServiceNavigationSettings.value = function () {
        return instance.getUserSettings({
          key: makePath(key)
        });
      };
      setUserServiceNavigationSettings.value = function (data) {
        return instance.setUserSettings({
          key: makePath(key),
          value: data
        });
      };
    }
    // immediate: true; because in some cases token already exist, and it will not change his value
  }, {
    immediate: true
  });
  const makePath = part => (part === null || part === void 0 ? void 0 : part.length) ? `${USER_SERVICE_NAVIGATION_SETTINGS_URL}.${part}` : USER_SERVICE_NAVIGATION_SETTINGS_URL;
  return {
    getUserServiceNavigationSettings,
    setUserServiceNavigationSettings
  };
}
var useUserServiceNavigationSettings$1 = createSharedComposable(useUserServiceNavigationSettings);

function useIdwService(token, config) {
  const isIdwAccount = ref(false);
  const idwInfo = ref(undefined);
  watch(token, async val => {
    if (val === null || val === void 0 ? void 0 : val.length) {
      const instance = new IdwApi({
        token: val,
        ...config
      });
      // in case if IDW service is fall, we should not break the navigation
      try {
        isIdwAccount.value = await instance.idwInfoApi.isIdwAccount();
      } catch (e) {
        isIdwAccount.value = false;
      }
      if (isIdwAccount.value) {
        idwInfo.value = await instance.idwInfoApi.getBaseInfo();
      }
    }
    // immediate: true; because in some cases token already exist, and it will not change his value
  }, {
    immediate: true
  });
  return {
    isIdwAccount,
    idwInfo
  };
}
var useIdwService$1 = createSharedComposable(useIdwService);

const HIDDEN_SERVICES_KEY = 'hidden_services';
function useAccountSettings(token, config) {
  let instance;
  const hiddenServices = ref([]);
  const getSettings = async () => {
    if (!instance) return;
    return await instance.getSettingsByKey(SERVICE_NAVIGATION_ACCOUNT_SETTING_KEY).catch(console.warn);
  };
  const getHiddenServices = async () => {
    if (!instance) return [];
    const settings = await getSettings();
    return settings ? settings[HIDDEN_SERVICES_KEY] : [];
  };
  const setAccountSettings = async (settingKey, settingsValue) => {
    if (!instance) return;
    return instance.setSettingsByKey(SERVICE_NAVIGATION_ACCOUNT_SETTING_KEY, {
      [settingKey]: settingsValue
    });
  };
  const hideService = async serviceId => {
    if (!instance) return;
    hiddenServices.value.push(serviceId);
    await setAccountSettings(HIDDEN_SERVICES_KEY, hiddenServices.value);
    hiddenServices.value = await getHiddenServices();
  };
  const showService = async serviceId => {
    if (!instance) return;
    const originalHiddenServices = [...hiddenServices.value];
    hiddenServices.value = originalHiddenServices.filter(id => id !== serviceId);
    await setAccountSettings(HIDDEN_SERVICES_KEY, hiddenServices.value.filter(id => id !== serviceId));
    hiddenServices.value = await getHiddenServices();
  };
  watch(token, async val => {
    if (!(val === null || val === void 0 ? void 0 : val.length)) return;
    instance = new AccountSettings({
      token: val,
      ...config
    });
    hiddenServices.value = await getHiddenServices();
  }, {
    immediate: true
  });
  return {
    hiddenServices,
    hideService,
    showService
  };
}
var useAccountSettings$1 = createSharedComposable(useAccountSettings);

export { FAVORITES as F, useAccountSettings$1 as a, useIdwService$1 as b, useUserServiceNavigationSettings$1 as c, useServiceDiscoveryUiServices$1 as u };
